// src/components/staff/StaffModal.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  Avatar,
  Alert,
  Box,
} from "@mui/material";
import {
  Staff,
  StaffPayload,
  createStaff,
  updateStaff,
  uploadStaffPhoto,
  deleteStaffPhoto,
  fetchServices,
  ServiceSummary,
} from "../../api/staff";
import StaffCalendarSelect from "./CalendarSelect";
import ServicesChipSelect from "./ServicesChipSelect";

const ALLOWED_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_FILE_SIZE = 5 * 1024 * 1024;

type StaffModalProps = {
  open: boolean;
  onClose: () => void;
  staff: Staff | null;
  onSaved: (staff: Staff) => void;
};

const StaffModal: React.FC<StaffModalProps> = ({
  open,
  onClose,
  staff,
  onSaved,
}) => {
  const isEditing = Boolean(staff);

  const [fullName, setFullName] = useState("");
  const [bio, setBio] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoDeleting, setPhotoDeleting] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [isActive, setIsActive] = useState(true);
  const [calendarId, setCalendarId] = useState<string | null>(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);

  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial values when editing/creating
  useEffect(() => {
    if (!open) return;

    setFullName(staff?.full_name || "");
    setBio(staff?.bio || "");
    setPhotoUrl(staff?.profile_photo_url || "");
    setPendingFile(null);
    setPendingPreview(null);
    setPhotoError(null);
    setIsActive(staff?.is_active ?? true);
    setCalendarId(staff?.calendar_id || null);

    const initialServiceIds =
      staff?.service_ids ||
      staff?.services?.map((svc) => svc.service_id) ||
      [];
    setSelectedServiceIds(initialServiceIds);
  }, [open, staff]);

  // Load services when modal opens
  useEffect(() => {
    if (!open) return;

    const loadMeta = async () => {
      try {
        setLoadingMeta(true);
        setError(null);
        const svcData = await fetchServices();
        setServices(svcData);
      } catch (err: any) {
        setError(err.message || "Failed to load staff metadata");
      } finally {
        setLoadingMeta(false);
      }
    };

    loadMeta();
  }, [open]);

  const handlePhotoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoError(null);

    if (!ALLOWED_TYPES.has(file.type)) {
      setPhotoError("Only PNG, JPEG and WebP images are supported.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setPhotoError("File must be under 5 MB.");
      return;
    }

    if (isEditing && staff) {
      setPhotoUploading(true);
      try {
        const url = await uploadStaffPhoto(staff.staff_id, file);
        setPhotoUrl(url);
      } catch {
        setPhotoError("Upload failed. Please try again.");
      } finally {
        setPhotoUploading(false);
      }
    } else {
      setPendingFile(file);
      setPendingPreview(URL.createObjectURL(file));
    }
  };

  const handlePhotoDelete = async () => {
    if (isEditing && staff) {
      setPhotoDeleting(true);
      try {
        await deleteStaffPhoto(staff.staff_id);
        setPhotoUrl("");
      } catch {
        setPhotoError("Failed to remove photo. Please try again.");
      } finally {
        setPhotoDeleting(false);
      }
    } else {
      setPendingFile(null);
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
      setPendingPreview(null);
    }
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      setError("Full name is required.");
      return;
    }

    const payload: StaffPayload = {
      full_name: fullName.trim(),
      bio: bio.trim() || null,
      profile_photo_url: photoUrl.trim() || null,
      is_active: isActive,
      calendar_id: calendarId ?? null,
      service_ids: selectedServiceIds,
    };

    try {
      setSaving(true);
      setError(null);

      let saved: Staff;
      if (isEditing && staff) {
        saved = await updateStaff(staff.staff_id, payload);
      } else {
        saved = await createStaff(payload);
        if (pendingFile) {
          try {
            const url = await uploadStaffPhoto(saved.staff_id, pendingFile);
            saved = { ...saved, profile_photo_url: url };
          } catch {
            // Photo upload failed — staff still created, surface non-fatal warning
            setError("Staff saved but photo upload failed. You can re-upload from edit.");
          }
        }
      }

      onSaved(saved);
      if (!error) onClose();
    } catch (err: any) {
      setError(err.message || "Failed to save staff member");
    } finally {
      setSaving(false);
    }
  };

  const title = isEditing ? "Edit Staff Member" : "Add Staff Member";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}

          {/* Full name */}
          <TextField
            label="Full name"
            fullWidth
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />

          {/* Bio */}
          <TextField
            label="Bio"
            fullWidth
            multiline
            minRows={2}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />

          {/* Profile photo upload */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Profile photo</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={pendingPreview || photoUrl || undefined}
                alt={fullName || "Profile"}
                sx={{ width: 52, height: 52 }}
              >
                {fullName?.charAt(0) || "?"}
              </Avatar>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: "none" }}
                onChange={handlePhotoFileChange}
                disabled={photoUploading || photoDeleting}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => photoInputRef.current?.click()}
                disabled={photoUploading || photoDeleting || saving}
                startIcon={photoUploading ? <CircularProgress size={14} /> : undefined}
              >
                {photoUploading ? "Uploading…" : (photoUrl || pendingPreview) ? "Replace" : "Upload"}
              </Button>
              {(photoUrl || pendingPreview) && (
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={handlePhotoDelete}
                  disabled={photoUploading || photoDeleting || saving}
                  startIcon={photoDeleting ? <CircularProgress size={14} /> : undefined}
                >
                  {photoDeleting ? "Removing…" : "Remove"}
                </Button>
              )}
            </Stack>
            {photoError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {photoError}
              </Typography>
            )}
          </Box>

          {/* Status */}
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label={isActive ? "Active" : "Inactive"}
          />

          {/* Calendar select (auto-filters used calendars) */}
          <StaffCalendarSelect
            staffId={staff?.staff_id}
            value={calendarId}
            onChange={setCalendarId}
          />

          {/* Services multi-select */}
          <ServicesChipSelect
            services={services}
            loading={loadingMeta}
            selectedIds={selectedServiceIds}
            onChange={setSelectedServiceIds}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffModal;
